import api from './api';

/**
 * 认证服务类 - 处理用户认证相关API请求
 */
class AuthService {
  /**
   * 用户登录
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @returns {Promise} 包含用户信息和token的Promise
   */
  async login(email, password) {
    try {
      const response = await api.post('/users/login', { email, password });
      this.setAuthToken(response.data.token);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 用户注册
   * @param {string} name - 用户名
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @returns {Promise} 包含用户信息和token的Promise
   */
  async register(name, email, password) {
    try {
      const response = await api.post('/users', { name, email, password });
      this.setAuthToken(response.data.token);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 用户登出
   */
  logout() {
    localStorage.removeItem('token');
    api.defaults.headers.common['Authorization'] = '';
  }

  /**
   * 验证当前用户token是否有效
   * @returns {Promise} 包含用户信息的Promise
   */
  async validateToken() {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error) {
      this.logout();
      throw this.handleError(error);
    }
  }

  /**
   * 获取当前用户信息
   * @returns {Promise} 包含用户信息的Promise
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 更新用户资料
   * @param {object} userData - 要更新的用户数据
   * @returns {Promise} 包含更新后用户信息的Promise
   */
  async updateProfile(userData) {
    try {
      const response = await api.put('/users/profile', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 更新用户密码
   * @param {string} currentPassword - 当前密码
   * @param {string} newPassword - 新密码
   * @returns {Promise} 包含更新结果的Promise
   */
  async updatePassword(currentPassword, newPassword) {
    try {
      const response = await api.put('/users/password', { currentPassword, newPassword });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 发送密码重置邮件
   * @param {string} email - 用户邮箱
   * @returns {Promise} 包含操作结果的Promise
   */
  async forgotPassword(email) {
    try {
      const response = await api.post('/users/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 重置密码
   * @param {string} token - 重置密码token
   * @param {string} newPassword - 新密码
   * @returns {Promise} 包含操作结果的Promise
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await api.post('/users/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 设置认证Token
   * @param {string} token - JWT令牌
   */
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }

  /**
   * 获取当前Token
   * @returns {string|null} JWT令牌或null
   */
  getAuthToken() {
    return localStorage.getItem('token');
  }

  /**
   * 处理API错误
   * @param {Error} error - API错误对象
   * @returns {Error} 格式化后的错误对象
   */
  handleError(error) {
    if (error.response) {
      // 服务器响应状态码超出 2xx 范围
      const errorMsg = error.response.data.message || '操作失败，请稍后再试';
      const customError = new Error(errorMsg);
      customError.statusCode = error.response.status;
      return customError;
    } else if (error.request) {
      // 请求已发送但没有收到响应
      return new Error('无法连接到服务器，请检查您的网络连接');
    } else {
      // 请求设置时发生错误
      return new Error('发送请求时出错，请稍后再试');
    }
  }

  /**
   * 模拟登录（开发环境使用）
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @returns {object} 模拟的用户数据
   */
  mockLogin(email, password) {
    if (email === 'demo@example.com' && password === 'password') {
      const userData = {
        id: '1',
        name: '演示用户',
        email: 'demo@example.com',
        role: 'user',
        token: 'mock-jwt-token'
      };
      
      this.setAuthToken(userData.token);
      return userData;
    }
    
    throw new Error('邮箱或密码错误');
  }
}

const authService = new AuthService();
export default authService; 